import { AxiosError } from "axios";
import type {
  AirconAddOnsDataType,
  AirconAddOnItem,
  AirconBookingFormDataType,
  BookingFormDataType,
  LockedPricing,
} from "../types/models";
import { format } from "date-fns";
import { availabilityAdapter, bookedSlotsAdapter } from "../utils/book";
import { api } from "./config/api";
import { BOOKING_MAX_DAYS_AHEAD } from "../constants/booking.js";
import { captureAppError, shouldCaptureServiceError, type SafeContext } from "../observability/sentry";
import { type ApiErrorBody } from "./config/extractError";

type ValidateSelectedSlotPayload = {
  service_id: number;
  cleaner_id: number;
  start_date: Date | string;
  start_time: string;
  end_time: string;
  frequency: string;
  postal?: string;
  promo_code?: string | null;
  skip_dynamic_pricing?: boolean;
  pricing_snapshot_id?: string;
  pricing_snapshot_slot_id?: string;
};

export type DraftBooking = {
  id: number;
  location: string;
  postal: string;
  floor_unit: string | null;
  date: string;
  start_time: string;
  end_time: string;
  status: "pending";
  frequency: string;
  notes: string | null;
  service_id: number;
  num_aircon_units: number | null;
  service_name: string;
  cleaner_id: number;
  cleaner_name: string;
  amount: number | null;
  original_amount: number | null;
  promo_discount_amount: number | null;
  promo_code: string | null;
  created_at: string;
  expires_in_seconds: number;
};

function getAxiosErrorBody(error: AxiosError): ApiErrorBody {
  const data = error.response?.data;
  return data && typeof data === "object" ? (data as ApiErrorBody) : {};
}

function getApiErrorMessage(error: unknown, fallback: string) {
  if (error instanceof AxiosError) {
    const body = getAxiosErrorBody(error);
    return body.error ?? body.message ?? fallback;
  }

  return fallback;
}

function captureBookingServiceError(
  error: unknown,
  action: string,
  extra?: SafeContext,
) {
  if (!shouldCaptureServiceError(error)) return;
  captureAppError(error, {
    area: "booking-service",
    action,
    extra,
  });
}

// Pricing
export async function releasePriceLock(): Promise<{ error: string | null }> {
  try {
    await api.delete("/pricing/lock");
    return { error: null };
  } catch {
    return { error: "Failed to release price lock" };
  }
}

// Availability
export async function fetchBookingAvailability(
  service_id: number,
  num_aircon_units?: number,
  daysAhead: number = BOOKING_MAX_DAYS_AHEAD,
  postal?: string,
) {
  try {
    const result = await api.get("/availability/preload", {
      params: {
        service_id,
        days: daysAhead,
        num_aircon_units,
        postal,
        _t: Date.now(),
      },
    });

    if (Array.isArray(result.data)) {
      return {
        slots: availabilityAdapter(result.data),
        bookedSlots: bookedSlotsAdapter(result.data),
        error: null,
      };
    }

    return {
      slots: null,
      error: "Failed to fetch available slots, please try again later.",
    };
  } catch (err) {
    captureBookingServiceError(err, "fetch-availability", {
      service_id,
      num_aircon_units: num_aircon_units ?? null,
      days_ahead: daysAhead,
      has_postal: Boolean(postal),
    });
    return {
      slots: null,
      error: getApiErrorMessage(
        err,
        "Failed to fetch available slots, please try again later.",
      ),
    };
  }
}

function formatSelectedSlotDate(date: Date | string) {
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  return format(new Date(date), "yyyy-MM-dd");
}

export async function validateSelectedSlot(payload: ValidateSelectedSlotPayload) {
  try {
    const response = await api.post("/availability/validate-selected-slot", {
      ...payload,
      start_date: formatSelectedSlotDate(payload.start_date),
      postal: payload.postal?.trim() || undefined,
      promo_code: payload.promo_code?.trim() || undefined,
    });

    return {
      available: response.data?.available === true,
      error: null,
      errorCode: null,
      pricing: (response.data?.pricing as LockedPricing) ?? null,
    };
  } catch (error: unknown) {
    captureBookingServiceError(error, "validate-selected-slot", {
      service_id: payload.service_id,
      cleaner_id: payload.cleaner_id,
      frequency: payload.frequency,
    });

    if (error instanceof AxiosError) {
      if (!error.response) {
        const isTimeout = error.code === "ECONNABORTED";
        return {
          available: false,
          error: isTimeout
            ? "Connection timed out while confirming your time slot. Your selection is still kept. Please try again."
            : "Connection error while confirming your time slot. Your selection is still kept. Please check your internet and try again.",
          errorCode: isTimeout ? "TIMEOUT" : "NETWORK_ERROR",
          pricing: null,
        };
      }

      const body = getAxiosErrorBody(error);
      return {
        available: false,
        error:
          body.error ??
          body.message ??
          "This time slot is no longer available. Please choose another time.",
        errorCode: body.code ?? null,
        pricing: null,
      };
    }

    return {
      available: false,
      error: "We couldn't confirm this time slot. Your selection is still kept. Please try again.",
      errorCode: "UNKNOWN_VALIDATION_ERROR",
      pricing: null,
    };
  }
}

export interface AvailableCleaner {
  id: number;
  name: string;
}

type FindAvailableCleanerPayload = {
  day: string;
  start_time: string;
  end_time: string;
  booking_date: string;
  frequency: string;
  service: "General Cleaning" | "Aircon Servicing";
  postal?: string;
};

// Used both to resolve a display name for the already-selected cleaner_id and,
// on a booking conflict, to find a replacement cleaner for the same date/time.
export async function findAvailableCleaner(
  payload: FindAvailableCleanerPayload,
): Promise<{ cleaners: AvailableCleaner[]; error: string | null }> {
  try {
    const response = await api.post("/find-available-cleaner", payload);
    return {
      cleaners: (response.data?.cleaners ?? []) as AvailableCleaner[],
      error: null,
    };
  } catch (error: unknown) {
    captureBookingServiceError(error, "find-available-cleaner", {
      frequency: payload.frequency,
      service: payload.service,
    });
    return {
      cleaners: [],
      error: getApiErrorMessage(error, "Failed to find an available cleaner."),
    };
  }
}

export async function fetchOneBooking(booking_id: number) {
  try {
    const response = await api.get(`/get-one-booking/${booking_id}`);

    if (response.data.booking) {
      return { booking: response.data.booking, error: null };
    }

    return { booking: null, error: "Failed to fetch one booking." };
  } catch (err) {
    captureBookingServiceError(err, "fetch-one-booking", { booking_id });
    return { booking: null, error: "Failed to fetch one booking." };
  }
}

export async function resendFailedWhatsAppMessages() {
  try {
    const response = await api.post("/bookings/whatsapp/resend-failed");
    const data = response.data as {
      success?: boolean;
      resent?: number;
      error?: string;
    };
    const success = data.success !== false;
    return {
      success,
      resent: data.resent ?? 0,
      error: success
        ? null
        : data.error ?? "Failed to resend WhatsApp messages.",
    };
  } catch (err) {
    captureBookingServiceError(err, "resend-failed-whatsapp");
    return { success: false, resent: 0, error: "Failed to resend WhatsApp messages." };
  }
}

/**
 * Sends this booking's invoice to the customer's confirmed email (KAN-622).
 *
 * Needed because the invoice is dispatched from the Stripe webhook, which fires as payment
 * confirms -- so a customer who adds their address on the success page is always too late for the
 * invoice they just generated. It has already gone out over WhatsApp.
 *
 * The server refuses an address that has not been confirmed, and `code` carries which case it
 * was: EMAIL_NOT_VERIFIED means click the link, EMAIL_NOT_SET means add one, INVOICE_NOT_READY
 * means the webhook is still running and this is worth retrying in a moment.
 */
export async function emailBookingInvoice(bookingId: number) {
  try {
    const response = await api.post(`/bookings/${bookingId}/email-invoice`);
    return {
      success: true,
      email: (response.data?.email as string | undefined) ?? null,
      error: null,
      code: null as string | null,
    };
  } catch (err) {
    const code =
      err instanceof AxiosError
        ? ((err.response?.data?.code as string | undefined) ?? null)
        : null;
    const serverMessage =
      err instanceof AxiosError
        ? (err.response?.data?.error as string | undefined)
        : undefined;

    // A refused send -- unconfirmed address, invoice still generating -- is the endpoint working
    // as designed, so it is not reported. Only the unexplained failures are.
    if (!code) {
      captureBookingServiceError(err, "email-booking-invoice", {
        booking_id: bookingId,
      });
    }

    return {
      success: false,
      email: null,
      error: serverMessage ?? "We couldn't email your invoice. Please try again.",
      code,
    };
  }
}

export interface PreviousCleaner {
  id: number;
  name: string;
  lastCleanDate: string;
  sessionCount: number;
}

export async function getPreviousCleaners(): Promise<{ cleaners: PreviousCleaner[]; error: null } | { cleaners: null; error: string }> {
  try {
    const response = await api.get("/previous-cleaners");
    return { cleaners: response.data.cleaners as PreviousCleaner[], error: null };
  } catch {
    return { cleaners: null, error: "Failed to fetch previous cleaners." };
  }
}

// Fetch bookings
export async function fetchBookings() {
  try {
    const response = await api.get("/view-booking-homeowner");

    if (Array.isArray(response.data.bookings)) {
      return { bookings: response.data.bookings, error: null };
    }

    return { bookings: null, error: "Failed to fetch bookings." };
  } catch (err) {
    captureBookingServiceError(err, "fetch-homeowner-bookings");
    return { bookings: null, error: "Failed to fetch bookings." };
  }
}

export async function fetchDraftBookings() {
  try {
    const response = await api.get("/draft");

    if (Array.isArray(response.data.drafts)) {
      return { drafts: response.data.drafts as DraftBooking[], error: null };
    }

    return { drafts: [] as DraftBooking[], error: "Failed to fetch draft bookings." };
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 401) {
      return { drafts: [] as DraftBooking[], error: null };
    }
    captureBookingServiceError(err, "fetch-draft-bookings");
    return { drafts: [] as DraftBooking[], error: "Failed to fetch draft bookings." };
  }
}

export async function discardDraftBooking(bookingId: number) {
  try {
    await api.delete(`/bookings/${bookingId}/draft`);
    return { error: null };
  } catch (error: unknown) {
    return { error: getApiErrorMessage(error, "Failed to discard booking.") };
  }
}

export async function fetchBookingsStaff() {
  try {
    const response = await api.get("/view-booking-cleaner");
    return {
      bookings: Array.isArray(response.data.bookings)
        ? response.data.bookings
        : [],
      error: null,
    };
  } catch (err) {
    captureBookingServiceError(err, "fetch-staff-bookings");
    return { bookings: null, error: "Failed to fetch staff bookings." };
  }
}

// Stripe checkout
export async function createCheckoutSession(
  booking_data: BookingFormDataType | AirconBookingFormDataType,
  cancel_url: string,
  promo_code?: string | null,
  auto_checkout_enabled: boolean = true,
  // How many referral-sourced hours to redeem — null means uncapped (apply as many as
  // available), 0 means the user opted out entirely, a positive number caps it.
  referral_hours_to_apply: number | null = null,
) {
  try {
    const response = await api.post("/create-checkout-session", {
      booking_data: {
        ...booking_data,
        start_date: format(new Date(booking_data.start_date), "yyyy-MM-dd"),
      },
      promo_code,
      auto_checkout_enabled,
      referral_hours_to_apply,
      success_url: `${window.location.origin}/book/return`,
      cancel_url,
    });

    return {
      url: response.data.url,
      bookingId: response.data.bookingId,
      flow: response.data.flow as
        | "hosted_checkout"
        | "auto_checkout_pending"
        | undefined,
      error: null,
      errorCode: null,
    };
  } catch (error: unknown) {
    const hasDraftReservation =
      "draft_reservation_booking_id" in booking_data &&
      Boolean(booking_data.draft_reservation_booking_id);

    captureBookingServiceError(error, "create-checkout-session", {
      service_id: Number(booking_data.service_id),
      frequency: String(booking_data.frequency ?? ""),
      auto_checkout_enabled,
      has_promo: Boolean(promo_code),
      has_draft_reservation: hasDraftReservation,
    });

    if (error instanceof AxiosError) {
      // Network / CORS / no-response error
      if (!error.response) {
        return {
          url: null,
          flow: undefined,
          error: "Connection error. Please check your internet connection.",
          errorCode: "NETWORK_ERROR",
        };
      }

      // Backend responded with error
      const body = getAxiosErrorBody(error);
      return {
        url: null,
        flow: undefined,
        error: body.error ?? body.message ?? "Failed to create checkout session. Please try again later.",
        errorCode: body.code ?? null,
      };
    }

    return {
      url: null,
      flow: undefined,
      error: "Failed to create checkout session. Please try again later.",
      errorCode: "UNKNOWN_CHECKOUT_ERROR",
    };
  }
}

// Booking actions
export async function rescheduleBooking(
  newDate: Date,
  newStart: string,
  newEnd: string,
  sessionId: number,
) {
  try {
    const response = await api.post(`/bookings/reschedule/${sessionId}`, {
      newDate: format(newDate, "yyyy-MM-dd"),
      newStart,
      newEnd,
      successUrl: `${window.location.origin}/dashboard?reschedulePayment=success&sessionId=${sessionId}`,
      cancelUrl: `${window.location.origin}/dashboard?reschedulePayment=cancelled&sessionId=${sessionId}`,
    });

    return {
      data: response.data ?? { message: "Booking successfully rescheduled" },
      error: null,
      errorCode: null,
    };
  } catch (error: unknown) {
    const message = getApiErrorMessage(
      error,
      "Failed to reschedule booking. Please try again later.",
    );
    const errorCode =
      error instanceof AxiosError ? getAxiosErrorBody(error).code ?? null : null;

    return {
      data: null,
      error: message,
      errorCode,
    };
  }
}

export type RescheduleSurchargeStatus =
  | { status: "confirmed"; paymentId?: number }
  | { status: "pending"; expiresAt?: string }
  | { status: "not_found_or_expired" };

export async function fetchRescheduleSurchargeStatus(sessionId: number) {
  try {
    const response = await api.get(`/bookings/reschedule/${sessionId}/status`);
    return {
      data: response.data as RescheduleSurchargeStatus,
      error: null,
    };
  } catch (error: unknown) {
    return {
      data: null,
      error: getApiErrorMessage(
        error,
        "Failed to confirm the reschedule status. Please check this booking again shortly.",
      ),
    };
  }
}

export async function checkRescheduleEligibility(sessionId: number) {
  try {
    const response = await api.get(`/bookings/reschedule/eligibility/${sessionId}`);
    return response.data as {
      canReschedule: boolean;
      canModify: boolean;
      canCancel: boolean;
      freeCancelUsed?: boolean;
      reason?: string;
      rescheduleCount?: number;
      isPackageSession?: boolean;
    };
  } catch (err) {
    captureBookingServiceError(err, "check-reschedule-eligibility", { session_id: sessionId });
    return { canReschedule: true, canModify: true, canCancel: true };
  }
}

// Statuses where the backend's message is written for the customer and says what actually
// happened (already cancelled, not cancellable, not found). Anything else (500, network, auth)
// is not a reason to show, and the outcome may be uncertain, so callers keep a generic message.
const CUSTOMER_FACING_ERROR_STATUSES = [404, 409, 422];

export async function TerminateBooking(bookingId: number, frequency: string) {
  try {
    const response = await api.patch(`/bookings/cancel/${bookingId}`, {
      frequency,
    });

    return {
      data: response.data?.message ?? "Booking successfully cancelled.",
      error: null,
      knownError: false,
    };
  } catch (error: unknown) {
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    return {
      data: null,
      error: getApiErrorMessage(error, "Failed to cancel booking."),
      knownError:
        status !== undefined && CUSTOMER_FACING_ERROR_STATUSES.includes(status),
    };
  }
}

/**
 * Where the early termination fee stands after cancelling a weekly/fortnightly plan.
 *
 * The fee is charged by a background worker after the cancel request has already returned, so a
 * declined card can't come back as an error on that request. The cancel dialog asks here instead.
 *   no_fee     nothing was owed, or it was waived
 *   processing the charge hasn't finished yet; ask again shortly
 *   paid       charged
 *   declined   the charge failed. The plan IS cancelled; `failureMessage` is Stripe's reason for
 *              the customer and `payUrl` is Stripe's page to pay with another card
 */
export type BookingTerminationOutcome = {
  state: "no_fee" | "processing" | "paid" | "declined";
  amountCents: number | null;
  failureMessage: string | null;
  payUrl: string | null;
};

export async function fetchBookingTermination(bookingId: number): Promise<{
  data: BookingTerminationOutcome | null;
  error: string | null;
}> {
  try {
    const response = await api.get(`/bookings/${bookingId}/termination`);
    return { data: response.data as BookingTerminationOutcome, error: null };
  } catch (error: unknown) {
    captureBookingServiceError(error, "fetch-booking-termination", { booking_id: bookingId });
    return {
      data: null,
      error: getApiErrorMessage(error, "Failed to check the early termination fee."),
    };
  }
}

export async function TerminateSession(sessionId: number) {
  try {
    const response = await api.patch(`/bookings/cancel/session/${sessionId}`);

    return {
      data: response.data?.message ?? "Session successfully cancelled.",
      error: null,
    };
  } catch (error: unknown) {
    return {
      data: null,
      error: getApiErrorMessage(error, "Failed to cancel session."),
    };
  }
}

// Remarks
export async function updateRemarks(sessionId: number, remarks: string) {
  try {
    await api.post(`/update-aircon-report-remarks`, {
      description: remarks,
      sessionId,
    });

    return { message: "Successfully updated remarks!", error: null };
  } catch (err) {
    captureBookingServiceError(err, "update-aircon-remarks", { session_id: sessionId });
    return {
      message: null,
      error: "Failed to update technican remarks. Please try again later.",
    };
  }
}

export async function fetchPreviousRemarks(
  bookingId: number,
  sessionId: number,
) {
  try {
    const res = await api.get(`/remarks/history/${bookingId}/${sessionId}`);
    return { remarks: res.data.remarks, error: null };
  } catch (err) {
    captureBookingServiceError(err, "fetch-previous-remarks", {
      booking_id: bookingId,
      session_id: sessionId,
    });
    return { remarks: null, error: "Failed to load remarks" };
  }
}

// Downloads
async function readDownloadError(err: unknown, fallback: string) {
  const responseData = (err as { response?: { data?: unknown } })?.response?.data;

  try {
    let text = "";
    if (responseData instanceof ArrayBuffer) {
      text = new TextDecoder().decode(responseData);
    } else if (responseData instanceof Blob) {
      text = await responseData.text();
    } else if (typeof responseData === "string") {
      text = responseData;
    } else if (responseData && typeof responseData === "object") {
      const body = responseData as { error?: string; message?: string };
      return body.error ?? body.message ?? fallback;
    }

    if (text) {
      const body = JSON.parse(text) as { error?: string; message?: string };
      return body.error ?? body.message ?? fallback;
    }
  } catch {
    // Fall through to the generic message if the error body is not JSON.
  }

  return fallback;
}

export async function downloadReport(
  sessionId: number,
  scope: "session" | "full" = "full",
) {
  try {
    const response = await api.get(`/download-aircon-report/${sessionId}`, {
      params: { scope },
      responseType: "arraybuffer",
    });

    return {
      data: new Blob([response.data], { type: "application/pdf" }),
      error: null,
    };
  } catch (err) {
    captureBookingServiceError(err, "download-aircon-report", {
      session_id: sessionId,
      scope,
    });
    return {
      data: null,
      error: await readDownloadError(
        err,
        "Failed to download service report. Please try again later.",
      ),
    };
  }
}

export async function downloadInvoice(sessionId: number) {
  try {
    const response = await api.get(`/download-receipt/${sessionId}`, {
      responseType: "arraybuffer",
    });

    if (!response.data || response.data.byteLength === 0) {
      return {
        data: null,
        error: "Failed to download receipt. Please try again later.",
      };
    }

    return {
      data: new Blob([response.data], { type: "application/pdf" }),
      error: null,
    };
  } catch (err) {
    captureBookingServiceError(err, "download-invoice", { session_id: sessionId });
    return {
      data: null,
      error: await readDownloadError(
        err,
        "Failed to download receipt. Please try again later.",
      ),
    };
  }
}

export async function downloadPaymentReceipt(paymentId: number) {
  try {
    const response = await api.get(`/download-payment-receipt/${paymentId}`, {
      responseType: "arraybuffer",
    });

    if (!response.data || response.data.byteLength === 0) {
      return {
        data: null,
        error: "Failed to download receipt. Please try again later.",
      };
    }

    return {
      data: new Blob([response.data], { type: "application/pdf" }),
      error: null,
    };
  } catch (err) {
    captureBookingServiceError(err, "download-payment-receipt", { payment_id: paymentId });
    return {
      data: null,
      error: await readDownloadError(
        err,
        "Failed to download receipt. Please try again later.",
      ),
    };
  }
}

export async function downloadQuarterlyAirconInvoice(bookingId: number) {
  try {
    const response = await api.get(`/download-quarterly-aircon-receipt/${bookingId}`, {
      responseType: "arraybuffer",
    });

    if (!response.data || response.data.byteLength === 0) {
      return {
        data: null,
        error: "Failed to download receipt. Please try again later.",
      };
    }

    return {
      data: new Blob([response.data], { type: "application/pdf" }),
      error: null,
    };
  } catch (err) {
    captureBookingServiceError(err, "download-quarterly-aircon-invoice", {
      booking_id: bookingId,
    });
    return {
      data: null,
      error: await readDownloadError(
        err,
        "Failed to download receipt. Please try again later.",
      ),
    };
  }
}

export async function fetchAddOnPaymentStatus(sessionId: number): Promise<{
  allConfirmed: boolean;
  hasPending: boolean;
} | null> {
  try {
    const response = await api.get(`/aircon-addons/payment-status/${sessionId}`);
    return response.data as { allConfirmed: boolean; hasPending: boolean };
  } catch (err) {
    captureBookingServiceError(err, "fetch-addon-payment-status", { session_id: sessionId });
    return null;
  }
}

export async function downloadAddOnReceipt(sessionId: number) {
  try {
    const response = await api.get(`/download-addon-receipt/${sessionId}`, {
      responseType: "arraybuffer",
    });

    if (!response.data || response.data.byteLength === 0) {
      return {
        data: null,
        error: "Failed to download add-on receipt. Please try again later.",
      };
    }

    return {
      data: new Blob([response.data], { type: "application/pdf" }),
      error: null,
    };
  } catch (err) {
    captureBookingServiceError(err, "download-addon-receipt", { session_id: sessionId });
    return {
      data: null,
      error: await readDownloadError(
        err,
        "Failed to download add-on receipt. Please try again later.",
      ),
    };
  }
}

export async function getAirconAddOns(booking_session_id: number) {
  try {
    const response = await api.get(`/aircon-addons/${booking_session_id}`);
    return { data: response.data.addons as AirconAddOnItem[], error: null };
  } catch (error: unknown) {
    captureBookingServiceError(error, "fetch-aircon-addons", {
      booking_session_id,
    });
    return { data: null, error: "Failed to fetch add-ons. Please try again." };
  }
}

export type PendingAddOnItem = {
  id: number;
  service_type: string;
  description: string | null;
  unit_price: number;
  quantity: number;
  totalcost: number;
  expires_at: string;
  payment_id: number;
};

export type PendingAddOnSession = {
  booking_session_id: number;
  booking_id: number;
  session_date: string;
  start_time: string;
  end_time: string;
  location: string;
  floor_unit: string | null;
  frequency: string;
  remark: string | null;
  remark_author: string | null;
  remark_updated_at: string | null;
  pending_promo_code: string | null;
  items: PendingAddOnItem[];
};

export async function fetchPendingAddOns() {
  try {
    const response = await api.get("/aircon-addons/pending/homeowner");
    return { data: response.data.sessions as PendingAddOnSession[], error: null };
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.status === 401) {
      return { data: null, error: null };
    }
    captureBookingServiceError(error, "fetch-pending-addons");
    return { data: null, error: "Failed to fetch pending add-ons." };
  }
}

export async function cancelAddOns(booking_session_id: number, addon_ids?: number[]) {
  try {
    await api.post("/aircon-addons/cancel", { booking_session_id, addon_ids });
    return { error: null };
  } catch (error: unknown) {
    captureBookingServiceError(error, "cancel-addons", {
      booking_session_id,
      addon_count: addon_ids?.length ?? null,
    });
    return { error: getApiErrorMessage(error, "Failed to cancel add-ons. Please try again.") };
  }
}

export async function createAddOnCheckout(
  booking_session_id: number,
  promoCode?: string,
  autoCheckoutEnabled?: boolean,
) {
  try {
    const response = await api.post("/aircon-addons/checkout", {
      booking_session_id,
      success_url: `${window.location.origin}/my-cart?addon_paid=1&session=${booking_session_id}`,
      cancel_url: `${window.location.origin}/my-cart`,
      ...(promoCode && { promo_code: promoCode }),
      ...(autoCheckoutEnabled !== undefined && { auto_checkout_enabled: autoCheckoutEnabled }),
    });
    return {
      url: (response.data.url as string) ?? null,
      flow: response.data.flow as "hosted_checkout" | "auto_checkout_pending",
      bookingSessionId: response.data.bookingSessionId as number | undefined,
      error: null,
    };
  } catch (error: unknown) {
    captureBookingServiceError(error, "create-addon-checkout", {
      booking_session_id,
      has_promo: Boolean(promoCode),
      auto_checkout_enabled: autoCheckoutEnabled ?? null,
    });
    return {
      url: null,
      flow: null,
      bookingSessionId: undefined,
      error: getApiErrorMessage(error, "Failed to initiate payment. Please try again."),
    };
  }
}

export async function getAddOnPaymentStatus(sessionId: number) {
  try {
    const response = await api.get(`/aircon-addons/payment-status/${sessionId}`);
    return {
      data: response.data as { allConfirmed: boolean; hasPending: boolean },
      error: null,
    };
  } catch (error: unknown) {
    return { data: null, error: getApiErrorMessage(error, "Failed to check payment status.") };
  }
}

export async function uploadAirconAddOns(
  data: AirconAddOnsDataType,
  booking_session_id: number,
) {
  try {
    const response = await api.post("/upload-aircon-addons", {
      booking_session_id,
      items: data.items,
    });
    return { data: response.data, error: null };
  } catch (error: unknown) {
    captureBookingServiceError(error, "upload-aircon-addons", {
      booking_session_id,
      addon_count: data.items.length,
    });
    return {
      data: null,
      error: getApiErrorMessage(error, "Failed to upload aircon add-ons. Please try again later."),
    };
  }
}

export async function fetchRescheduleWindow(
  sessionId: number,
): Promise<{
  cycleStart: string | null;
  cycleEnd: string | null;
  packageExpiresAt?: string | null;
  /** "YYYY-MM-DD", the date the session was first booked for; a move may go 84 days past it. */
  originalSessionDate?: string | null;
} | null> {
  try {
    const res = await api.get(`/bookings/reschedule/window/${sessionId}`);
    return res.data;
  } catch (err) {
    captureBookingServiceError(err, "fetch-reschedule-window", { session_id: sessionId });
    return null;
  }
}

export type VerifyBookingPaymentErrorCode =
  | "BOOKING_NOT_FOUND"
  | "INVALID_BOOKING_ID";

export type VerifyBookingPaymentData = {
  status: string;
  bookingId?: number;
  reason?: string;
};

export async function verifyBookingPayment(bookingId: number): Promise<{
  data: VerifyBookingPaymentData | null;
  error: string | null;
  code?: VerifyBookingPaymentErrorCode;
  status?: number;
}> {
  try {
    const res = await api.get(`/checkout/verify/booking/${bookingId}`);
    return { data: res.data, error: null };
  } catch (err) {
    captureBookingServiceError(err, "verify-booking-payment", { booking_id: bookingId });
    if (err instanceof AxiosError) {
      const body = getAxiosErrorBody(err);
      const code = body.code as
        | VerifyBookingPaymentErrorCode
        | undefined;
      const message = body.error ?? body.message ?? "Failed to verify payment status.";

      return {
        data: null,
        error: message,
        code,
        status: err.response?.status,
      };
    }

    return { data: null, error: "Failed to verify payment status." };
  }
}

export async function resumeBookingCheckout(payload: {
  booking_id: number;
  cancel_url: string;
}): Promise<{
  data: { url: string; bookingId: number; flow: "hosted_checkout" } | null;
  error: string | null;
  code?: string | null;
}> {
  try {
    const res = await api.post<{ url: string; bookingId: number; flow: "hosted_checkout" }>(
      `/bookings/${payload.booking_id}/resume-checkout`,
      { cancel_url: payload.cancel_url },
    );
    return { data: res.data, error: null, code: null };
  } catch (err) {
    captureBookingServiceError(err, "resume-booking-checkout", { booking_id: payload.booking_id });
    const message = getApiErrorMessage(err, "Failed to resume booking checkout. Please try again.");
    const code = err instanceof AxiosError ? (getAxiosErrorBody(err).code ?? null) : null;
    return { data: null, error: message, code };
  }
}

export async function checkPackageUpsellEligibility(options: {
  source?: "promo_unlock";
  packageDefinitionId?: number | null;
} = {}): Promise<{
  data: { eligible: boolean } | null;
  error: string | null;
}> {
  try {
    const params = {
      ...(options.source ? { source: options.source } : {}),
      ...(options.packageDefinitionId ? { package_definition_id: options.packageDefinitionId } : {}),
    };
    const res = await api.get<{ eligible: boolean }>("/upsell/package-eligibility", {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return { data: res.data, error: null };
  } catch {
    return { data: null, error: "Failed to check eligibility" };
  }
}
